import { useCallback, useEffect, useState } from 'react';
import { get, patch, post } from '@vigilo/api-client';
import { useMockMode } from '@/shell/MockModeContext';
import {
  CROWD_PAGINATION_DEFAULT_PAGE_SIZE,
  type CrowdPaginationEnvelope,
} from '../components/PaginationControls';

export type CrowdTeamStatus = 'active' | 'archived';
export type TeamArchiveStatus = CrowdTeamStatus | 'all';

export interface CrowdTeam {
  id: string;
  applicationId: string;
  name: string;
  description: string | null;
  color: string | null;
  status: CrowdTeamStatus;
  memberUserIds: string[];
  createdBy: string | null;
  archivedBy: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TeamPayload {
  name: string;
  description?: string | null;
  color?: string | null;
  memberUserIds?: string[];
}

export interface TeamsHookState {
  teams: CrowdTeam[];
  pagination: CrowdPaginationEnvelope;
  loading: boolean;
  error: string | null;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  createTeam: (payload: TeamPayload) => Promise<void>;
  updateTeam: (id: string, payload: TeamPayload) => Promise<void>;
  archiveTeam: (id: string) => Promise<void>;
  refresh: (archiveStatus?: TeamArchiveStatus) => Promise<void>;
}

const MOCK_TEAMS: CrowdTeam[] = [
  {
    id: '44444444-0000-4000-8000-000000000001',
    applicationId: 'crowd_monitoring',
    name: 'Alpha Team',
    description: 'Main entrance and merchandise area coverage',
    color: '#4f9f9b',
    status: 'active',
    memberUserIds: ['user-001', 'user-004'],
    createdBy: 'user-005',
    archivedBy: null,
    archivedAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: '44444444-0000-4000-8000-000000000002',
    applicationId: 'crowd_monitoring',
    name: 'Bravo Team',
    description: 'North stand coverage',
    color: '#d8a63f',
    status: 'active',
    memberUserIds: ['user-002'],
    createdBy: 'user-005',
    archivedBy: null,
    archivedAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: '44444444-0000-4000-8000-000000000003',
    applicationId: 'crowd_monitoring',
    name: 'Charlie Team',
    description: 'South stand coverage',
    color: '#a66ad8',
    status: 'active',
    memberUserIds: ['user-003'],
    createdBy: 'user-005',
    archivedBy: null,
    archivedAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

function unwrap<T>(body: unknown): T {
  const wrapped = body as { data?: T };
  return (wrapped.data ?? body) as T;
}

function unwrapList<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const wrapped = body as { data?: T[] };
  return wrapped.data ?? [];
}

function unwrapPaginatedList<T>(body: unknown, fallbackPage: number, fallbackPageSize: number): {
  rows: T[];
  pagination: CrowdPaginationEnvelope;
} {
  const candidate = body && typeof body === 'object' && 'data' in body
    ? (body as { data?: unknown }).data
    : body;
  const envelope = candidate && typeof candidate === 'object' && 'data' in candidate
    ? candidate as { data?: T[]; total?: number; page?: number; pageSize?: number; hasNextPage?: boolean }
    : null;

  if (envelope && Array.isArray(envelope.data)) {
    return {
      rows: envelope.data,
      pagination: {
        total: Number(envelope.total ?? envelope.data.length),
        page: Number(envelope.page ?? fallbackPage),
        pageSize: Number(envelope.pageSize ?? fallbackPageSize),
        hasNextPage: Boolean(envelope.hasNextPage),
      },
    };
  }

  const rows = unwrapList<T>(body);
  return {
    rows,
    pagination: {
      total: rows.length,
      page: fallbackPage,
      pageSize: fallbackPageSize,
      hasNextPage: false,
    },
  };
}

export function useTeams(): TeamsHookState {
  const { isMock } = useMockMode();
  const [teams, setTeams] = useState<CrowdTeam[]>(() => isMock ? MOCK_TEAMS.slice(0, CROWD_PAGINATION_DEFAULT_PAGE_SIZE) : []);
  const [pagination, setPagination] = useState<CrowdPaginationEnvelope>(() => ({
    total: isMock ? MOCK_TEAMS.length : 0,
    page: 1,
    pageSize: CROWD_PAGINATION_DEFAULT_PAGE_SIZE,
    hasNextPage: isMock ? CROWD_PAGINATION_DEFAULT_PAGE_SIZE < MOCK_TEAMS.length : false,
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeValue] = useState(CROWD_PAGINATION_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(!isMock);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (archiveStatus: TeamArchiveStatus = 'active') => {
    if (isMock) {
      const filtered = MOCK_TEAMS.filter(team => archiveStatus === 'all' || team.status === archiveStatus);
      const offset = (page - 1) * pageSize;
      setTeams(filtered.slice(offset, offset + pageSize));
      setPagination({
        total: filtered.length,
        page,
        pageSize,
        hasNextPage: page * pageSize < filtered.length,
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        archiveStatus,
        page: String(page),
        pageSize: String(pageSize),
      });
      const body = await get<unknown>(`/teams?${query.toString()}`);
      const result = unwrapPaginatedList<CrowdTeam>(body, page, pageSize);
      setTeams(result.rows);
      setPagination(result.pagination);
    } catch {
      setError('Failed to load teams.');
    } finally {
      setLoading(false);
    }
  }, [isMock, page, pageSize]);

  useEffect(() => { void refresh(); }, [refresh]);

  const setPageSize = useCallback((nextPageSize: number) => {
    setPageSizeValue(Math.max(1, Math.trunc(nextPageSize)));
    setPage(1);
  }, []);

  const createTeam = useCallback(async (payload: TeamPayload) => {
    if (isMock) {
      const now = new Date().toISOString();
      const team: CrowdTeam = {
        id: `team-${Date.now()}`,
        applicationId: 'crowd_monitoring',
        name: payload.name.trim(),
        description: payload.description?.trim() || null,
        color: payload.color?.trim() || null,
        status: 'active',
        memberUserIds: payload.memberUserIds ?? [],
        createdBy: 'mock-user',
        archivedBy: null,
        archivedAt: null,
        createdAt: now,
        updatedAt: now,
      };
      setTeams(prev => [...prev, team].sort((a, b) => a.name.localeCompare(b.name)));
      return;
    }
    const body = await post<CrowdTeam | { data?: CrowdTeam }>('/teams', payload);
    const team = unwrap<CrowdTeam>(body);
    if (team) {
      await refresh();
    }
  }, [isMock, refresh]);

  const updateTeam = useCallback(async (id: string, payload: TeamPayload) => {
    if (isMock) {
      const now = new Date().toISOString();
      setTeams(prev => prev.map(team => team.id === id ? {
        ...team,
        name: payload.name.trim(),
        description: payload.description?.trim() || null,
        color: payload.color?.trim() || null,
        memberUserIds: payload.memberUserIds ?? [],
        updatedAt: now,
      } : team).sort((a, b) => a.name.localeCompare(b.name)));
      return;
    }
    const body = await patch<CrowdTeam | { data?: CrowdTeam }>(`/teams/${id}`, payload);
    const team = unwrap<CrowdTeam>(body);
    if (team) {
      await refresh();
    }
  }, [isMock, refresh]);

  const archiveTeam = useCallback(async (id: string) => {
    if (isMock) {
      const now = new Date().toISOString();
      setTeams(prev => prev.map(team => team.id === id ? {
        ...team,
        status: 'archived' as const,
        archivedBy: 'mock-user',
        archivedAt: now,
        memberUserIds: [] as string[],
        updatedAt: now,
      } : team).filter(team => team.status === 'active'));
      return;
    }
    const body = await post<CrowdTeam | { data?: CrowdTeam }>(`/teams/${id}/archive`, {});
    const team = unwrap<CrowdTeam>(body);
    if (team) {
      await refresh();
    }
  }, [isMock, refresh]);

  return { teams, pagination, loading, error, setPage, setPageSize, createTeam, updateTeam, archiveTeam, refresh };
}
